import { and, eq, or } from "drizzle-orm";
import { Link, redirect } from "react-router";
import { z } from "zod";
import { formatDate } from "~/components/site/Date";
import { UserSidebar } from "~/components/site/user/UserSiderbar";
import { Breadcrumb } from "~/components/ui/Breadcrumb";
import { PageTitle } from "~/components/ui/PageTitle";
import { Table } from "~/components/ui/Table";
import { pg } from "~/pg.server";
import { useT, useTurl } from "~/util/Lang";
import { $LOADER, $PAGE } from "~/util/Loaders";
import { logPageView } from "~/util/stats";
import { getTitle } from "~/util/wiki";

const Params = z.object({ userId: z.string() });

$PAGE({
  path: "users/:userId/pages",
  loader: $LOADER?.(async (cx) => {
    // Log
    logPageView(cx);
    //
    const params = Params.parse(cx.params);
    const user = await pg.db
      .select({
        id: pg.users.id,
        login: pg.users.login,
        createdAt: pg.users.createdAt,
      })
      .from(pg.users)
      .where(eq(pg.users.id, parseInt(params.userId)))
      .limit(1)
      .then((res) => res[0]);
    if (!user) {
      return redirect("/");
    }
    const pages = await pg.db
      .select({
        id: pg.mdPages.id,
        slug: pg.mdPages.wikiSlug,
        game: pg.mdPages.game,
        public: pg.userPages.public,
        createdAt: pg.mdPages.createdAt,
        revision: {
          titleEn: pg.mdRevisions.titleEn,
          contentEn: pg.mdRevisions.contentEn,
          titleRu: pg.mdRevisions.titleRu,
          contentRu: pg.mdRevisions.contentRu,
          createdAt: pg.mdRevisions.createdAt,
        },
      })
      .from(pg.mdPages)
      .where(
        and(
          eq(pg.userPages.userId, user.id),
          eq(pg.mdPages.isDeleted, false),
          or(
            eq(pg.userPages.public, true),
            and(
              eq(pg.userPages.public, false),
              eq(pg.userPages.userId, cx.req.sess?.user.id ?? 0),
            ),
          ),
        ),
      )
      .innerJoin(pg.userPages, eq(pg.userPages.id, pg.mdPages.draftId))
      .innerJoin(pg.mdRevisions, eq(pg.mdRevisions.id, pg.mdPages.revisionId));

    return { authUser: cx.req.sess?.user, user, pages };
  }),

  meta: (args) => {
    return {
      title: `${args.t({
        en: "Pages",
        ru: "Страницы",
      })} - ${args.data.user.login} - ${args.t({
        en: "Users",
        ru: "Пользователи",
      })} - L2Hub.info`,
      canonicalPath: `/users/${args.data.user.id}/pages`,
    };
  },

  render: ({ data }) => {
    const { user, authUser, pages } = data;
    const t = useT();
    const turl = useTurl();
    const pageTitle = t({ en: "Pages", ru: "Страницы" });
    const breadcrumbList = [
      { title: t({ en: "Users", ru: "Пользователи" }), url: turl("/users") },
      {
        title: user.login,
        url: turl(`/users/${user.id}`),
      },
      {
        title: pageTitle,
        url: turl(`/users/${user.id}/pages`),
      },
    ];
    return (
      <div className="content">
        <Breadcrumb noRoot={true} items={breadcrumbList} />
        <div className="columns">
          <div className="column is-three-quarters">
            <PageTitle>{pageTitle}</PageTitle>

            <Table
              data={pages}
              id={(page) => page.id}
              cols={[
                {
                  title: t({ en: "Title", ru: "Название" }),
                  cell: (page) => (
                    <>
                      <Link to={turl(`/users/${user.id}/pages/${page.id}`)}>
                        {t(
                          getTitle(
                            page.revision.titleEn,
                            page.revision.titleRu,
                          ),
                        )}
                      </Link>{" "}
                      {page.public ? "(Public)" : "(Private)"}
                    </>
                  ),
                },
                {
                  width: 150,
                  title: t({ en: "Published", ru: "Опубликован" }),
                  cell: (page) => formatDate(page.createdAt),
                },
              ]}
            />
          </div>
          <div className="column">
            <UserSidebar counters={{ comments: 0 }} user={user} />
          </div>
        </div>
      </div>
    );
  },
});
